import { useEffect, useRef } from 'react';
import { getAcombagoOrigins } from '../utils/portal/portalConfig';

/**
 * Calls `onMessage` every time the AcombaGO host posts a message of `messageType`, handing it the
 * validated message object so a caller that needs the payload (not just the signal) can read it.
 *
 * The message crosses a window boundary, so both the origin and the payload shape are untrusted: the
 * sender is only believed once its origin matches a configured AcombaGO origin, and a primitive `data`
 * would otherwise pass the cast and fall through silently. Centralising those checks here means a caller
 * that only wants to re-run a refresh can ignore the argument, and one that reads counts (see
 * `useRecordTranspherePaymentsApplied`) does not re-implement the trust checks.
 *
 * The callback is held in a ref so an inline arrow function does not re-subscribe on every render.
 */
export const useRefreshOnPortalMessage = (
    messageType: string,
    onMessage: (message: Record<string, unknown>) => void,
): void => {
    const onMessageRef = useRef(onMessage);

    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        function handleMessage(event: MessageEvent<unknown>): void {
            const origins = getAcombagoOrigins();
            if (origins.length === 0 || !origins.includes(event.origin)) return;

            const data = event.data;
            if (typeof data !== 'object' || data === null) return;
            const message = data as Record<string, unknown>;
            if (message.type !== messageType) return;

            onMessageRef.current(message);
        }

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [messageType]);
};
